import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import TradeSubmittedModal from '@/components/core-v2/organisms/trade-submitted-modal'
import { PATH } from '@/const'
import { UserRole, UserType } from '@/types/user'

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { TradeActivationStep } from '../../types'
import { NoteStep } from '../01-note/note'
import { TermsStep } from '../02-terms/pages'
import { PortfolioStep } from '../03-portfolio/pages'
import { StrategyStep } from '../04-strategy/pages'
import { CompleteStep } from '../05-complete/pages'
import { actions, StepName } from './state/builder-slice'
import { WizardLayout } from './wizard-layout'

function getActiveStep(step: StepName) {
  switch (step) {
    case 'portfolio': {
      return <PortfolioStep />
    }
    case 'terms': {
      return <TermsStep />
    }
    case 'strategy': {
      return <StrategyStep />
    }
    case 'complete': {
      return <CompleteStep />
    }
    default: {
      return <NoteStep />
    }
  }
}

type BuilderProps = {
  accountId?: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export function Builder({ accountId, userName, userRoles, userType }: BuilderProps) {
  const dispatch = useAppDispatch()
  const state = useAppSelector(state => state.builder)
  const navigate = useNavigate()

  useEffect(() => {
    dispatch(
      actions.init({
        accountId,
        userName,
        userRoles,
        userType
      })
    )
  }, [dispatch, accountId, userName, userRoles, userType])

  useEffect(() => {
    const isAlreadySavedAccount =
      state.status !== undefined &&
      state.status !== TradeActivationStep.draft &&
      state.status !== TradeActivationStep.rejected &&
      state.status !== TradeActivationStep.expired
    if (state.selectedAccountId && (state.isFinished || isAlreadySavedAccount)) {
      dispatch(actions.setApprovalNotification({ title: 'Trade created' }))
    }
  }, [state.selectedAccountId, state.isFinished, state.status, dispatch, navigate])

  const onNotificationClose = () => {
    dispatch(actions.resetNotification())
    navigate(PATH.trades)
  }

  return (
    <WizardLayout
      currentStepName={state.step}
      onGoToStep={stepName => dispatch(actions.goToStep(stepName))}
    >
      {getActiveStep(state.step)}
      {state && state.approvalNotification && (
        <TradeSubmittedModal
          acceptCaption="Back to all draft trades"
          open={state.approvalNotification !== undefined}
          steps={['Submit', 'Economics', 'Approval', 'Commitment', 'Settlement', 'Activate']}
          onAccept={() => onNotificationClose()}
        />
      )}
    </WizardLayout>
  )
}
